/**
 * routes/api.auth.$.ts
 * ====================
 * Explicit server endpoints that wrap Amazon Cognito (replaces the better-auth
 * catch-all handler). The browser auth client (lib/auth/auth-client.ts) talks to
 * these; all token exchange happens server-side and tokens are stored in
 * httpOnly cookies (lib/auth/cookies.server.ts) so they never touch JS.
 *
 *   POST /api/auth/sign-in        {email,password}      → {status:'ok'|'mfa'}
 *   POST /api/auth/mfa/challenge  {code}                → {status:'ok'} (completes login MFA)
 *   POST /api/auth/sign-up        {email,password,name} → {status:'ok'|'confirm_required'}
 *   POST /api/auth/confirm-sign-up {email,code,password} → {status:'ok'|'mfa'}
 *   POST /api/auth/resend-code    {email}                → {ok:true}
 *   POST /api/auth/sign-out                              → {ok:true}
 *   GET  /api/auth/session                               → {user, session, mfa} | {user:null}
 *   POST /api/auth/refresh                               → {ok:true} | 401
 *   POST /api/auth/mfa/enable                            → {secret, otpauthUri}
 *   POST /api/auth/mfa/verify     {code}                 → {ok:true}
 *   POST /api/auth/mfa/disable                           → {ok:true}
 *   POST /api/auth/forgot-password {email}               → {ok:true}
 *   POST /api/auth/reset-password  {email,code,password} → {ok:true}
 *   GET  /api/auth/google?redirect=/path                 → 302 to Cognito → Google
 *   GET  /api/auth/callback?code=&state=                 → exchange code, set cookies, 302
 */

import { createFileRoute } from '@tanstack/react-router';
import { setCookie, getCookie, deleteCookie } from '@tanstack/react-start/server';
import * as cognito from '@/lib/auth/cognito.server';
import {
  writeTokenCookies,
  clearTokenCookies,
  readTokenCookies,
} from '@/lib/auth/cookies.server';
import { resolveSession } from '@/lib/auth/verify.server';
import { getServerSiteOrigin } from '@/lib/site-url';
import { sendEmail, emailTemplates } from '@/server/email.server';

const MFA_COOKIE = 'he_mfa'; // interim login-MFA state (Cognito session + username)

/** Fire a transactional email without ever failing the auth flow it rides on.
 *  Awaited (so it runs before the Lambda freezes) but errors are swallowed. */
async function safeSend(
  to: string,
  payload: { subject: string; html?: string; text?: string },
): Promise<void> {
  try {
    await sendEmail({ to, ...payload });
  } catch (err) {
    console.error('[auth] transactional email failed:', (err as Error).message);
  }
}

function json(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { 'content-type': 'application/json' },
  });
}

function redirect(location: string): Response {
  return new Response(null, { status: 302, headers: { Location: location } });
}

/** Decode a JWT payload without verifying (callback tokens come straight from
 *  the trusted Cognito token endpoint over TLS). */
function decodeJwt(jwt: string): Record<string, unknown> {
  try {
    return JSON.parse(Buffer.from(jwt.split('.')[1], 'base64url').toString('utf8'));
  } catch {
    return {};
  }
}

/** Sanitize a caller-supplied post-login redirect to a same-site path. */
function safePath(p: string | null | undefined, fallback = '/'): string {
  if (!p || !p.startsWith('/') || p.startsWith('//')) return fallback;
  return p;
}

/**
 * Public origin for the OAuth redirect_uri. The CloudFront viewer-request
 * function (heallexa-forward-host) forwards the real host as x-forwarded-host,
 * which lambda-entry bakes into the request URL — so each domain (prod, beta,
 * localhost) round-trips OAuth back to itself. Only when the Lambda is hit
 * directly on its function URL do we fall back to PUBLIC_SITE_URL; never hand
 * Cognito a lambda-url origin as the user-facing destination.
 */
function publicOrigin(url: URL): string {
  if (/\.lambda-url\.[^.]+\.on\.aws$/.test(url.hostname)) return getServerSiteOrigin();
  return url.origin;
}

async function handle(request: Request): Promise<Response> {
  const url = new URL(request.url);
  // Path after /api/auth/ — e.g. 'sign-in', 'mfa/challenge'.
  const action = url.pathname.replace(/^\/api\/auth\/?/, '').replace(/\/$/, '');
  const method = request.method.toUpperCase();

  const body = async () => {
    try {
      return (await request.json()) as Record<string, unknown>;
    } catch {
      return {};
    }
  };

  try {
    switch (`${method} ${action}`) {
      // ── Email + password sign-in ─────────────────────────────────────────
      case 'POST sign-in': {
        const { email, password } = await body();
        const result = await cognito.signIn(String(email), String(password));
        if (result.status === 'ok') {
          writeTokenCookies(result.tokens);
          return json({ status: 'ok' });
        }
        if (result.status === 'mfa_setup') {
          // Cognito issues MFA_SETUP only when the pool requires MFA but the user
          // has none enrolled. Our pools are OPTIONAL (enrollment is opt-in via
          // /auth/mfa-setup while signed in), so this should not occur — surface
          // it explicitly rather than sending the user to the verify screen.
          return json(
            { error: 'This account must finish two-factor setup before signing in.', code: 'MFA_SETUP' },
            400,
          );
        }
        // status === 'mfa': stash the Cognito challenge session for /mfa/challenge.
        setCookie(MFA_COOKIE, JSON.stringify({ session: result.session, username: result.username }), {
          httpOnly: true,
          secure: true,
          sameSite: 'lax',
          path: '/',
          maxAge: 60 * 5,
        });
        return json({ status: 'mfa' });
      }

      // ── Complete login TOTP challenge ────────────────────────────────────
      case 'POST mfa/challenge': {
        const { code } = await body();
        const raw = getCookie(MFA_COOKIE);
        if (!raw) return json({ error: 'No pending MFA challenge' }, 400);
        const { session, username } = JSON.parse(raw) as { session: string; username: string };
        const tokens = await cognito.respondTotpChallenge(username, String(code), session);
        writeTokenCookies(tokens);
        deleteCookie(MFA_COOKIE, { path: '/' });
        return json({ status: 'ok' });
      }

      // ── Sign-up (new Cognito-native user) ────────────────────────────────
      // Cognito emails a 6-digit code; no session is issued until the user
      // confirms it via /confirm-sign-up. (If the pool auto-confirms, we get
      // tokens straight back and sign them in.)
      case 'POST sign-up': {
        const { email, password, name } = await body();
        const result = await cognito.signUp(String(email), String(password), String(name ?? ''));
        if (result.status === 'ok') {
          // Pool auto-confirmed (no email-code step) → account is live now.
          writeTokenCookies(result.tokens);
          await safeSend(String(email), emailTemplates.welcome(name ? String(name) : undefined));
          return json({ status: 'ok' });
        }
        // 'confirm_required' (or an unexpected challenge status) — no cookies.
        return json({ status: result.status });
      }

      // ── Confirm a sign-up with the emailed 6-digit code, then sign in ─────
      // The browser still holds the password in form state and re-sends it so
      // we can mint a session immediately after confirmation (seamless, like
      // the old Supabase verifyOtp flow which returned a session directly).
      case 'POST confirm-sign-up': {
        const { email, code, password } = await body();
        await cognito.confirmSignUp(String(email), String(code));
        // Account is now verified/active → send the welcome email once.
        await safeSend(String(email), emailTemplates.welcome());
        const result = await cognito.signIn(String(email), String(password));
        if (result.status === 'ok') {
          writeTokenCookies(result.tokens);
          return json({ status: 'ok' });
        }
        if (result.status === 'mfa') {
          // A brand-new account won't have MFA, but stay correct if it does.
          setCookie(MFA_COOKIE, JSON.stringify({ session: result.session, username: result.username }), {
            httpOnly: true,
            secure: true,
            sameSite: 'lax',
            path: '/',
            maxAge: 60 * 5,
          });
          return json({ status: 'mfa' });
        }
        return json({ status: result.status });
      }

      // ── Re-send the sign-up verification code ────────────────────────────
      case 'POST resend-code': {
        const { email } = await body();
        await cognito.resendSignUpCode(String(email));
        return json({ ok: true });
      }

      // ── Sign-out ─────────────────────────────────────────────────────────
      case 'POST sign-out': {
        const { accessToken } = readTokenCookies();
        if (accessToken) await cognito.globalSignOut(accessToken);
        clearTokenCookies();
        deleteCookie(MFA_COOKIE, { path: '/' });
        return json({ ok: true });
      }

      // ── Current session ──────────────────────────────────────────────────
      case 'GET session': {
        const session = await resolveSession();
        if (!session) return json({ user: null, session: null });
        let enrolled = false;
        try {
          enrolled = await cognito.isTotpEnabled(session.accessToken);
        } catch {
          /* non-fatal */
        }
        return json({
          user: {
            id: session.userId,
            email: session.email,
            name: session.name,
            image: session.image,
          },
          session: { userId: session.userId, expiresAt: session.expiresAt },
          mfa: { enrolled },
        });
      }

      // ── Current access token (same-origin JS → edge functions) ───────────
      // The access token lives in an httpOnly cookie. Browser code that must
      // call the edge Lambda directly (cross-origin Bearer) fetches it here.
      // Refreshes transparently if expired. Only the short-lived access token
      // is exposed — never the refresh token.
      case 'GET token': {
        const session = await resolveSession();
        if (!session) return json({ token: null }, 401);
        return json({ token: session.accessToken });
      }

      // ── Refresh (rotate access/id tokens) ────────────────────────────────
      case 'POST refresh': {
        const session = await resolveSession(); // resolveSession refreshes + rewrites cookies
        if (!session) return json({ ok: false }, 401);
        return json({ ok: true });
      }

      // ── TOTP enrollment ──────────────────────────────────────────────────
      case 'POST mfa/enable': {
        const session = await resolveSession();
        if (!session) return json({ error: 'Unauthorized' }, 401);
        const { secret, otpauthUri } = await cognito.enrollTotp(
          session.accessToken,
          session.email ?? '',
        );
        return json({ secret, otpauthUri });
      }
      case 'POST mfa/verify': {
        const session = await resolveSession();
        if (!session) return json({ error: 'Unauthorized' }, 401);
        const { code } = await body();
        await cognito.verifyTotpEnrollment(session.accessToken, String(code));
        return json({ ok: true });
      }
      case 'POST mfa/disable': {
        const session = await resolveSession();
        if (!session) return json({ error: 'Unauthorized' }, 401);
        await cognito.disableTotp(session.accessToken);
        return json({ ok: true });
      }

      // ── Password reset ───────────────────────────────────────────────────
      case 'POST forgot-password': {
        const { email } = await body();
        await cognito.forgotPassword(String(email));
        return json({ ok: true });
      }
      case 'POST reset-password': {
        const { email, code, password } = await body();
        await cognito.confirmForgotPassword(String(email), String(code), String(password));
        // Security notification: tell the user their password just changed.
        await safeSend(String(email), emailTemplates.passwordChanged());
        return json({ ok: true });
      }

      // ── Google federation (redirect leg) ─────────────────────────────────
      case 'GET google': {
        const domain = process.env.COGNITO_DOMAIN;
        const clientId = process.env.COGNITO_CLIENT_ID;
        if (!domain || !clientId) return json({ error: 'Cognito not configured' }, 500);
        // Derived from the request host (see publicOrigin) so beta round-trips
        // to beta and prod to prod. Must exactly match a callback URL registered
        // in the Cognito app client (and the token-exchange redirect_uri below).
        const redirectUri = `${publicOrigin(url)}/api/auth/callback`;
        const state = safePath(url.searchParams.get('redirect'), '/post-login');
        const authorize =
          `${domain}/oauth2/authorize?identity_provider=Google&response_type=code` +
          `&client_id=${encodeURIComponent(clientId)}` +
          `&redirect_uri=${encodeURIComponent(redirectUri)}` +
          `&scope=${encodeURIComponent('openid email profile aws.cognito.signin.user.admin')}` +
          `&state=${encodeURIComponent(state)}`;
        return redirect(authorize);
      }

      // ── Google federation (callback leg) ─────────────────────────────────
      case 'GET callback': {
        const code = url.searchParams.get('code');
        const dest = safePath(url.searchParams.get('state'), '/post-login');
        if (!code) return redirect('/auth/login?error=oauth');
        // redirect_uri here must byte-match the one used in the authorize leg.
        const tokens = await exchangeCode(code, `${publicOrigin(url)}/api/auth/callback`);
        writeTokenCookies(tokens);
        // Federated (Google) users carry no custom:legacy_id. Provision/identity-
        // map them by email NOW so their original UUID (existing users) or a fresh
        // one (new users) + profiles/user_roles exist before /post-login runs.
        {
          const c = decodeJwt(tokens.idToken);
          if (!c['custom:legacy_id'] && c.email) {
            await cognito.provisionFederatedUser(
              String(c.email),
              String(c.name ?? ''),
              String(c.picture ?? ''),
            );
          }
        }
        return redirect(dest);
      }

      default:
        return json({ error: `Unknown auth action: ${method} ${action}` }, 404);
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Auth error';
    console.error('[api.auth]', method, action, message);
    // Map common Cognito failures to 4xx so the UI can show a useful message.
    const name = (err as { name?: string })?.name ?? '';
    const status =
      /NotAuthorized|UserNotFound|CodeMismatch|ExpiredCode|InvalidPassword|UsernameExists|InvalidParameter|LimitExceeded|TooManyRequests|AliasExists|CodeDeliveryFailure/.test(
        name,
      )
        ? 400
        : 500;
    return json({ error: message, code: name }, status);
  }
}

/** Exchange an OAuth authorization code for tokens at the Cognito token endpoint. */
async function exchangeCode(code: string, redirectUri: string) {
  const domain = process.env.COGNITO_DOMAIN!;
  const clientId = process.env.COGNITO_CLIENT_ID!;
  const clientSecret = process.env.COGNITO_CLIENT_SECRET!;
  const basic = Buffer.from(`${clientId}:${clientSecret}`).toString('base64');
  const res = await fetch(`${domain}/oauth2/token`, {
    method: 'POST',
    headers: {
      'content-type': 'application/x-www-form-urlencoded',
      authorization: `Basic ${basic}`,
    },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: clientId,
      code,
      redirect_uri: redirectUri,
    }),
  });
  if (!res.ok) throw new Error(`Token exchange failed: ${res.status} ${await res.text()}`);
  const data = (await res.json()) as {
    access_token: string;
    id_token: string;
    refresh_token: string;
  };
  const seg = data.id_token.split('.')[1];
  const claims = JSON.parse(Buffer.from(seg, 'base64url').toString('utf8')) as Record<string, unknown>;
  return {
    accessToken: data.access_token,
    idToken: data.id_token,
    refreshToken: data.refresh_token,
    username: (claims['cognito:username'] as string) ?? (claims.sub as string) ?? '',
  };
}

export const Route = createFileRoute('/api/auth/$')({
  server: {
    handlers: {
      GET: async ({ request }) => handle(request),
      POST: async ({ request }) => handle(request),
    },
  },
  component: () => null, // API route — no UI
});
